import { ref, Ref } from "vue";
import { AxiosPromise } from "axios";
import { useModule } from "@/application/composables/module";
import mitt from "@/application/plugins/mitt";

export const useShowRequestHandler = <T>(
  request: (parameters: Record<string, unknown>) => AxiosPromise<{ data: T }>,
  fallbackModule?: string,
) => {
  const { module } = useModule(fallbackModule);

  const data = ref<T>();

  let controller: undefined | AbortController;
  const isLoading = ref(false);
  const isInitialLoaded = ref(false);

  const handler = async () => {
    if (controller) {
      controller.abort();
    }
    controller = new AbortController();

    try {
      isLoading.value = true;

      const response = await request({
        signal: controller.signal,
      });

      data.value = response.data.data;

      isLoading.value = false;
      isInitialLoaded.value = true;
    } catch (error: any) {
      if (error.name === "CanceledError") return;
      isLoading.value = false;
      throw error;
    }
  };

  mitt.on("cancel:request", (cancelModule: string) => {
    if (cancelModule !== module || !controller) return;
    controller.abort();
  });

  return {
    refresh: handler,
    isLoading,
    isInitialLoaded,
    data: data as Ref<T>,
  };
};
